/**
 * BotConnectorsTab — what this agent may reach outside the platform, in four
 * sections: cloud storage (Google Drive), MCP servers, API connectors, wikis.
 * Each section exists only under its own tenant flag; the tab is rendered when
 * any of them is on (BotModal).
 *
 * Google Drive has TWO levels, because they are two different promises to the
 * person whose Drive it is:
 *
 *   1. `google_drive`      — read, search, browse folders, save NEW files.
 *                            Nothing that already exists is ever altered.
 *                            Absent means OFF (operator decision 2026-09-28:
 *                            it used to mean ON, and every agent silently had
 *                            all the Drive tools on every conversation). Since
 *                            2026-10-19 it means the agent ALLOWS Drive: each
 *                            user turns it on for themselves in the
 *                            conversation (connectors/AgentConnectors), or
 *                            automatically from Settings.
 *   2. `google_drive_edit` — additionally rewrite existing Docs / Sheets /
 *                            Slides and Office files in place. Absent means
 *                            OFF: switching Drive on must never silently hand
 *                            an agent the power to rewrite documents.
 *
 * Neither switch touches attaching files FROM Drive in the chat composer or
 * the Library's "From Google Drive" upload: those are the person's own act,
 * gated by the tenant flag and their own connection (`useDriveConnected`).
 *
 * Level 2 is OFFERED only where it can actually work: the organisation's flag
 * `integrations.cloud_storage.editing` (a super admin sets it on White-Label →
 * Connectors) — the only ceiling above the agent's own switch (operator
 * decision 2026-09-25). When it is off there is no row, not a disabled one:
 * the person editing an agent cannot change it, so a dead switch only ever
 * described a power they could not have. Where it is offered, the row appears
 * under the Drive card the moment Drive is switched on.
 *
 * The tenant flag `integrations.cloud_storage` is the ceiling above all of it:
 * with that off, nothing about Drive is rendered at all.
 *
 * MCP servers and APIs are ONE "Services" list with one "Add connector"
 * (operator decision 2026-10-21, option A): Add turns the tab into the
 * organization's connector list with a search (AgentConnectorBrowser) — each
 * entry with its own Add, nothing pre-selected — and "Not in the list?" opens
 * the custom MCP / API forms. Every connector says whether it comes from the
 * organization (follows its settings), is customized for this agent, or is
 * this agent's only; the "i" beside the title explains the three once. A
 * group admin who manages this agent's organization also sees a quiet
 * "Manage list" link.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Switch } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { useFeature } from '@hooks/useFeature';
import { GoogleDriveGlyph } from '@modules/conversations/components/drive/driveIcons';
import { useAgentAuthoringCapabilities } from '../hooks/useAgentAuthoringCapabilities';
import { useAgentConnectorLibrary } from '@modules/connector_library/hooks/useAgentConnectorLibrary';
import { StatusHelp } from '@modules/connector_library/components/LibraryBits';
import { AgentConnectorBrowser } from '@modules/connector_library/components/AgentConnectorBrowser';
import type { LibraryKind } from '@modules/connector_library/services/connectorLibraryService';
import { PlusGlyph, SectionHead } from './ConnectorDialog';
import McpConnectorsSection from './McpConnectorsSection';
import ApiConnectorsSection from './ApiConnectorsSection';
import WikiConnectorsSection, { type WikiIntegration } from './WikiConnectorsSection';
import './ConnectorDialog.css';
import './BotConnectorsTab.css';

export interface BotConnectors {
  google_drive?: boolean;
  google_drive_edit?: boolean;
}

interface Props {
  value: BotConnectors;
  onChange: (next: BotConnectors) => void;
  disabled?: boolean;
  /** The agent being edited; undefined until a new agent is saved. MCP and
      API connectors are records on the agent, so they need its id. */
  chatbotId?: string;
  /** The agent's wiki bindings (`handler_config.wikis`): they ride the form
      like the Drive switches, so the form owner holds and persists them. */
  wikis: WikiIntegration[];
  onWikisChange: (next: WikiIntegration[]) => void;
  onPersistWikis: () => Promise<void>;
}

export default function BotConnectorsTab({
  value, onChange, disabled, chatbotId, wikis, onWikisChange, onPersistWikis,
}: Props) {
  const { t } = useLanguage();
  const driveOffered = useFeature('integrations.cloud_storage');
  const mcpOffered = useFeature('integrations.mcp_connectors');
  const apiOffered = useFeature('integrations.api_connectors');
  const wikiOffered = useFeature('modules.wiki');
  const { driveTenantEditing, driveConnected, loaded } = useAgentAuthoringCapabilities(driveOffered);
  const library = useAgentConnectorLibrary(chatbotId);
  const { manageHref } = library;
  const kinds: LibraryKind[] = [...(mcpOffered ? ['mcp' as const] : []), ...(apiOffered ? ['api' as const] : [])];
  // Add → the organization's list, inside the tab; "Custom …" → that form.
  const [browsing, setBrowsing] = useState(false);
  const [custom, setCustom] = useState<LibraryKind | null>(null);
  const [mcpCount, setMcpCount] = useState<number | null>(null);
  const [apiCount, setApiCount] = useState<number | null>(null);
  const onMcpCount = useCallback((n: number) => setMcpCount(n), []);
  const onApiCount = useCallback((n: number) => setApiCount(n), []);
  const counted = (!mcpOffered || mcpCount !== null) && (!apiOffered || apiCount !== null);
  const servicesCount = (mcpCount ?? 0) + (apiCount ?? 0);
  const libraryLoading = !library.loaded;
  // The sections unmount while the Add list is open; their counts come back
  // with them, so the empty note never shows the counts from before.
  useEffect(() => { if (browsing) { setMcpCount(null); setApiCount(null); } }, [browsing]);

  // Absent = OFF: the same rule as `agent_drive_connector_enabled` on the server.
  const on = value?.google_drive === true;
  const editOn = value?.google_drive_edit === true;
  // Wait for `loaded` before deciding, or the row flashes in after the card.
  const editOffered = loaded && driveTenantEditing;
  const editEffective = on && editOffered && editOn;

  const toggle = () => onChange({ ...value, google_drive: !on });
  const toggleEdit = () => onChange({ ...value, google_drive_edit: !editOn });

  if (browsing && chatbotId) {
    return (
      <div className="cxt" data-testid="bot-connectors-tab">
        <AgentConnectorBrowser
          chatbotId={chatbotId}
          library={library}
          kinds={kinds}
          onBack={() => setBrowsing(false)}
          // The sections mount again on the way back and read their lists afresh.
          onAdded={() => setBrowsing(false)}
          onCustom={kind => { setBrowsing(false); setCustom(kind); }}
        />
      </div>
    );
  }

  return (
    <div className="cxt" data-testid="bot-connectors-tab">
      <div className="cxt__intro">
        <div className="cxt__intro-head">
          <span className="cxt__title-group">
            <h3 className="cxt__title">{t('bots.connectors.title')}</h3>
            {(mcpOffered || apiOffered) && <StatusHelp />}
          </span>
          {manageHref && (
            /* A new tab keeps the editor's unsaved work; rel="opener" (same origin) makes the
               browser copy this tab's sign-in, which lives in sessionStorage. */
            <a className="cxt__manage" href={manageHref} target="_blank" rel="opener"
               data-testid="connector-library-manage-link">
              {t('connectorLibrary.manageLink')}
            </a>
          )}
        </div>
        <p className="cxt__desc">{t('bots.connectors.description')}</p>
      </div>

      {driveOffered && (
        <section className="cxs" data-testid="drive-connector-section">
          <SectionHead icon={<CloudGlyph />} title={t('bots.connectors.cloudStorage')}
                       description={t('bots.connectors.driveAccountNote')} />

          <div className="cxs__list">
            <SwitchCard
              icon={<GoogleDriveGlyph size={24} />}
              name={t('bots.connectors.googleDrive')}
              description={t('bots.connectors.googleDriveHint')}
              on={on}
              disabled={disabled}
              onToggle={toggle}
              testId="connector-google-drive"
              switchTestId="connector-google-drive-switch"
              pills={on && (
                <span className="cx-pill cx-pill--accent" data-testid="drive-permission-level">
                  {t(`bots.connectors.level.${editEffective ? 'edit' : 'save'}`)}
                </span>
              )}
            />

            {on && editOffered && (
              <SwitchCard
                nested
                icon={<PencilGlyph />}
                name={t('bots.connectors.driveEditing')}
                description={t('bots.connectors.driveEditingHint')}
                on={editOn}
                disabled={disabled}
                onToggle={toggleEdit}
                testId="connector-google-drive-edit"
                switchTestId="connector-google-drive-edit-switch"
              />
            )}
          </div>

          {/* The precondition under everything: without a linked account the
              agent gets no Drive tools at all, whatever the switches say. */}
          {loaded && !driveConnected && (
            <p className="cxs__note" data-testid="drive-not-connected-note">
              {t('bots.connectors.notConnectedNote')}
            </p>
          )}
        </section>
      )}

      {(mcpOffered || apiOffered) && (
        <section className="cxs" data-testid="services-section">
          <SectionHead
            icon={<ServicesGlyph />}
            title={t('bots.connectors.services.title')}
            description={t('bots.connectors.services.description')}
            action={(
              <button type="button" className="cx-btn cx-btn--primary"
                      disabled={disabled || !chatbotId || libraryLoading} aria-busy={(!!chatbotId && libraryLoading) || undefined}
                      onClick={() => setBrowsing(true)} data-testid="services-add-connector">
                <PlusGlyph />
                {t('bots.connectors.services.add')}
              </button>
            )}
          />
          {!chatbotId && (
            <p className="cxs__note" data-testid="services-save-first-note">{t('bots.connectors.mcp.saveFirst')}</p>
          )}
          {chatbotId && counted && servicesCount === 0 && (
            <p className="cxs__note" data-testid="services-empty-note">{t('bots.connectors.services.empty')}</p>
          )}
          <div className="cxs__groups">
            {mcpOffered && (
              <McpConnectorsSection chatbotId={chatbotId} disabled={disabled} library={library}
                                    customAddOpen={custom === 'mcp'} onCustomAddClose={() => setCustom(null)}
                                    onCount={onMcpCount} />
            )}
            {apiOffered && (
              <ApiConnectorsSection chatbotId={chatbotId} disabled={disabled} library={library}
                                    customAddOpen={custom === 'api'} onCustomAddClose={() => setCustom(null)}
                                    onCount={onApiCount} />
            )}
          </div>
        </section>
      )}

      {wikiOffered && (
        <WikiConnectorsSection value={wikis} onChange={onWikisChange} onPersist={onPersistWikis} disabled={disabled} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The tab's own pieces
// ---------------------------------------------------------------------------

/** A card that IS a switch: the whole card toggles, the switch shows state. */
function SwitchCard({ icon, name, description, on, disabled, onToggle, testId, switchTestId, nested, pills }: {
  icon: ReactNode; name: string; description: string; on: boolean; disabled?: boolean; onToggle: () => void;
  testId: string; switchTestId: string; nested?: boolean; pills?: ReactNode;
}) {
  const { t } = useLanguage();
  return (
    <div
      className={`cxc cxc--switch ${on ? 'is-on' : ''} ${disabled ? 'is-disabled' : ''} ${nested ? 'cxc--nested' : ''}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onToggle()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onToggle();
        }
      }}
      data-testid={testId}
    >
      <span className="cxc__icon" aria-hidden="true">{icon}</span>
      <span className="cxc__body">
        <span className="cxc__name">{name}</span>
        <span className="cxc__desc">{description}</span>
        {pills && <span className="cxc__pills">{pills}</span>}
      </span>
      <span className="cxc__end">
        <span className={`cxc__state ${on ? 'is-on' : ''}`}>
          {on ? t('bots.connectors.on') : t('bots.connectors.off')}
        </span>
        <Switch
          checked={on}
          disabled={disabled}
          onChange={() => onToggle()}
          onClick={(_, e) => e.stopPropagation()}
          data-testid={switchTestId}
        />
      </span>
    </div>
  );
}

function ServicesGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2v6" />
      <path d="M15 2v6" />
      <path d="M6 8h12v3a6 6 0 0 1-12 0V8Z" />
      <path d="M12 17v5" />
    </svg>
  );
}

function CloudGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      <path d="M17.5 19a4.5 4.5 0 0 0 .5-8.97A6 6 0 0 0 6.3 8.5 4.5 4.5 0 0 0 7 19h10.5Z" />
    </svg>
  );
}

function PencilGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}
